import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Award, Trophy, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const BadgeCard = ({ badge, earned }) => (
  <div className={`relative p-4 rounded-xl border ${
    earned 
      ? 'bg-gradient-to-br from-amber-900/30 to-amber-700/20 border-amber-500/50' 
      : 'bg-white/5 border-white/10 opacity-60'
  }`}>
    {/* Badge Icon */}
    <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center text-3xl ${
      earned 
        ? 'bg-gradient-to-br from-amber-500 to-amber-700' 
        : 'bg-white/10'
    }`}>
      {earned ? badge.icon : <Lock size={24} className="text-white/40" />}
    </div>
    
    {/* Badge Info */}
    <h3 className={`text-center font-bold mb-1 ${earned ? 'text-white' : 'text-white/50'}`}>
      {badge.name}
    </h3>
    <p className={`text-center text-sm mb-2 ${earned ? 'text-white/70' : 'text-white/40'}`}>
      {badge.description}
    </p>
    
    {/* Progress Bar - only for locked badges with progress */}
    {!earned && badge.progress !== null && badge.progress !== undefined && (
      <div className="mt-3">
        <div className="flex justify-between text-xs text-white/50 mb-1">
          <span>{badge.progress}%</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-amber-500 rounded-full transition-all"
            style={{ width: `${Math.min(badge.progress, 100)}%` }}
          />
        </div>
      </div>
    )}
    
    {/* Earned checkmark */}
    {earned && (
      <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
        <span className="text-white text-sm">✓</span>
      </div>
    )}
  </div>
);

const BadgesPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [badges, setBadges] = useState({ earned: [], locked: [], stats: {} });
  const [loading, setLoading] = useState(true);

  const fetchBadges = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/badges/user/${user.id}`);
      // API returns { badges: [...], stats: {...} }
      // We need to separate into earned and locked
      const allBadges = res.data.badges || [];
      const earned = allBadges.filter(b => b.earned);
      const locked = allBadges.filter(b => !b.earned);
      setBadges({ 
        earned, 
        locked, 
        stats: res.data.stats || {} 
      });
    } catch (error) {
      console.error('Failed to fetch badges');
      setBadges({ earned: [], locked: [], stats: {} });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchBadges();
    } else {
      setLoading(false);
    }
  }, [user?.id, fetchBadges]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to view your badges</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col pb-24">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8 flex items-center justify-center gap-2">
          <Award className="text-amber-500" size={24} />
          My Badges
        </h1>
      </div>

      {/* Stats Summary */}
      <div className="p-4 bg-gradient-to-r from-amber-900/20 to-transparent border-b border-white/10">
        <div className="flex items-center justify-center gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-500">{badges.earned.length}</p>
            <p className="text-white/50 text-sm">Earned</p>
          </div>
          <div className="w-px h-12 bg-white/20" />
          <div className="text-center">
            <p className="text-3xl font-bold text-white/50">{badges.locked.length}</p>
            <p className="text-white/50 text-sm">Locked</p>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : (
          <>
            {/* Earned Badges */}
            {badges.earned.length > 0 && (
              <div className="mb-6">
                <h2 className="text-white font-bold mb-3 flex items-center gap-2">
                  <Trophy className="text-amber-500" size={20} />
                  Earned Badges
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {badges.earned.map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} earned={true} />
                  ))}
                </div>
              </div>
            )}

            {/* Locked Badges */}
            {badges.locked.length > 0 && (
              <div>
                <h2 className="text-white/50 font-bold mb-3 flex items-center gap-2">
                  <Lock size={20} />
                  Locked Badges
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {badges.locked.map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} earned={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default BadgesPage;
